/**
 * Text formatting helpers shared by all pages
 */

/**
 * Convert a snake_case identifier to Title Case, e.g.
 * "certificate_renewal" -> "Certificate Renewal"
 */
export function humanize(value: string | null | undefined): string {
  if (!value) return '';
  return value
    .replace(/_/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * "1 node", "2 nodes", "0 nodes"
 */
export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/**
 * Format an origin as a URL, omitting the default port for the scheme, e.g.
 * "https://api.example.com" or "http://10.0.0.1:8080"
 */
export function formatOriginUrl(origin: { address: string; port: number; enable_tls: boolean }): string {
  const protocol = origin.enable_tls ? 'https' : 'http';
  const defaultPort = origin.enable_tls ? 443 : 80;
  if (origin.port === defaultPort) {
    return `${protocol}://${origin.address}`;
  }
  return `${protocol}://${origin.address}:${origin.port}`;
}

/**
 * Format an X.509 distinguished name, e.g. "CN=example.com, O=Org, C=US"
 */
export function formatDistinguishedName(dn: {
  common_name?: string;
  organization?: string[];
  country?: string[];
}): string {
  const parts: string[] = [];
  if (dn.common_name) parts.push(`CN=${dn.common_name}`);
  if (dn.organization?.length) parts.push(`O=${dn.organization.join(', ')}`);
  if (dn.country?.length) parts.push(`C=${dn.country.join(', ')}`);
  return parts.join(', ') || '-';
}

/**
 * Format an audit log action, e.g. ("create_node", "node") -> "create node node"
 */
export function formatAuditAction(action: string, resourceType: string): string {
  return `${action.replace(/_/g, ' ')} ${resourceType}`;
}

/**
 * Single-character symbol for an audit action
 */
export function getAuditActionSymbol(action: string): string {
  if (action.includes('create') || action.includes('add')) return '+';
  if (action.includes('delete') || action.includes('remove')) return '-';
  if (action.includes('update') || action.includes('edit')) return '~';
  if (action.includes('reload') || action.includes('restart')) return '!';
  return '>';
}

const NUMBER_LOCALE = 'en-US';

const integerFormat = new Intl.NumberFormat(NUMBER_LOCALE, { maximumFractionDigits: 0 });

const compactFormat = new Intl.NumberFormat(NUMBER_LOCALE, {
  notation: 'compact',
  maximumFractionDigits: 1,
});

/**
 * Full count with thousands separators, e.g. 1234567 -> "1,234,567"
 */
export function formatNumber(value: number): string {
  return integerFormat.format(value);
}

/**
 * Compact count for tight spaces, e.g. 950 -> "950", 1234 -> "1.2K", 3400000 -> "3.4M"
 */
export function formatCompactNumber(value: number): string {
  return compactFormat.format(value);
}

/**
 * A 0..1 ratio as a percentage with at most one decimal, e.g. 0.0123 -> "1.2%".
 * Tiny non-zero shares render as "<0.1%" so they never read as zero.
 */
export function formatPercent(ratio: number): string {
  if (!Number.isFinite(ratio) || ratio <= 0) return '0%';
  const percent = ratio * 100;
  if (percent < 0.1) return '<0.1%';
  return `${Number(percent.toFixed(1))}%`;
}

/**
 * A duration in seconds as a short interval label, e.g. 60 -> "1 min", 900 -> "15 min",
 * 21600 -> "6 h", 86400 -> "1 d"
 */
export function formatInterval(seconds: number): string {
  if (seconds >= 86_400 && seconds % 86_400 === 0) return `${seconds / 86_400} d`;
  if (seconds >= 3600 && seconds % 3600 === 0) return `${seconds / 3600} h`;
  if (seconds >= 60 && seconds % 60 === 0) return `${seconds / 60} min`;
  return `${seconds} s`;
}
